import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import {
  type ApplicationConfig,
  type EnvironmentProviders,
  ErrorHandler,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  type Provider,
  provideZoneChangeDetection,
} from '@angular/core';
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling, withRouterConfig } from '@angular/router';

import { firstValueFrom, map, of, type Observable } from 'rxjs';

import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { TUI_DATE_FORMAT, TUI_ICON_RESOLVER } from '@taiga-ui/core';
import { NG_EVENT_PLUGINS } from '@taiga-ui/event-plugins';

import {
  AD_AUTH_TOKEN,
  authEffects,
  AuthenticationFacadeService,
  AuthenticationTokenStorageService,
  authFeature,
  MfaApolloLinkFactory,
  mfaHttpInterceptor,
  provideActiveDirectoryAuthDataAccess,
  provideFinAuthenticationConfiguration,
  userActivityEffects,
  userActivityFeature,
} from '@finverity/authentication';
import { TranslocoService, provideTranslocoScope } from '@jsverse/transloco';

import { environment } from '@finverity/platform-app/env/environment';
import { provideFinFileUploaderConfiguration } from '@finverity/file-uploader';
import { provideFinApolloGraphqlOptions } from '@finverity/graphql';
import { provideAppTransloco } from '@finverity/i18n';
import { I18N_DEBUG_TRANSPILER_PROVIDER } from './i18n-debug.transpiler';
import { FVICON_TO_PHOSPHOR, LUCIDE_TO_PHOSPHOR } from './core/icons/phosphor-icon-map';
import { TENANT_PHOSPHOR_OVERRIDES, TENANT_ICON_NAME_OVERRIDES } from './core/icons/tenant-icon-overrides';
import {
  type Permission,
  type Policy,
  provideFinIdentityAccessPermissions,
  provideFinIdentityAccessPolicies,
} from '@finverity/identity-access';
import { APP_ROUTES } from '@finverity/platform-app/app.routes';
import { FirebaseCloudMessagingService } from '@finverity/platform-app/core/firebase';
import {
  authInterceptor,
  blobErrorInterceptor,
  errorHandlerInterceptor,
} from '@finverity/platform-app/core/http-interceptors';
import {
  ACTIVE_LOCALE_PROVIDER,
  attemptKerberosAutoLogin,
  LOCALE_APP_INITIALIZER,
  TUI_LANGUAGE_PROVIDER,
} from '@finverity/platform-app/core/providers';
import { SentryErrorHandlerService, SentryReporterService } from '@finverity/platform-app/core/sentry';
import { FinEnvironmentService, NotificationsService, StonlyService } from '@finverity/platform-app/core/services';
import { debugActionsMetaReducer } from '@finverity/platform-app/core/store';
import { getAllowedDomainsList } from '@finverity/platform-app/core/xss-security/helpers';
import { FILE_UPLOAD_RULES } from '@finverity/platform-app/shared/constants';
import { CurrenciesService } from '@finverity/platform-app/shared/services';
import { CONTRACT_ACTIONS_CURRENT_COMPANY_ID } from '@fv/ui-kit/legacy-for-migration/contract-actions';
import { SAFE_LINK_ALLOWED_DOMAINS, provideFvUiKitDefaultOptions } from '@fv/ui-kit';
import { FV_DATA_GRID_TEXTS, type FvDataGridTexts } from '@fv/ui-kit/components/data-grid';

const ANGULAR_SPECIFIC_PROVIDERS: Array<Provider | EnvironmentProviders> = [
  provideZoneChangeDetection(),
  provideAnimations(),
  provideHttpClient(
    withFetch(),
    withInterceptors([errorHandlerInterceptor, blobErrorInterceptor, authInterceptor, mfaHttpInterceptor])
  ),
  provideRouter(APP_ROUTES, withRouterConfig({}), withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })),
  // Firebase is opt-out for air-gap deployments where FCM cannot reach Google's servers.
  ...(environment.firebase.enabled === false
    ? []
    : [
        provideFirebaseApp(() => {
          const environmentService = inject(FinEnvironmentService);
          return initializeApp(environmentService.firebase);
        }),
        provideMessaging(() => getMessaging(getApp())),
      ]),
  provideAppTransloco(),
  provideTranslocoScope({ scope: 'shell' }),
  provideTranslocoScope({ scope: 'authentication' }),
  provideTranslocoScope({ scope: 'instrument' }),
  provideTranslocoScope({ scope: 'instrument-details' }),
  provideTranslocoScope({ scope: 'documents-management' }),
  provideTranslocoScope({ scope: 'bulk-supporting-documents-upload' }),
  // Shared libs that translate from their own services (root-provided → eager load).
  provideTranslocoScope({ scope: 'finverity-file-uploader' }),
  provideTranslocoScope({ scope: 'finverity-graphql' }),
  LOCALE_APP_INITIALIZER,
  ACTIVE_LOCALE_PROVIDER,
  // i18n pseudo-localization debug transpiler — toggle via localStorage.i18nDebug=1 (visual QA only).
  I18N_DEBUG_TRANSPILER_PROVIDER,
];

// TENANT_PHOSPHOR_OVERRIDES is empty unless a white-label build swaps the map in via project.json.
const tenantPhosphorPath = (phosphor: string): string =>
  TENANT_PHOSPHOR_OVERRIDES[phosphor] ?? `assets/phosphor/${phosphor}.svg`;

// A 'fvIcon<Name>' map value is a bespoke asset in assets/svg-icons; any other value is a Phosphor
// filename, which a white-label build may override via tenantPhosphorPath.
const resolveMappedIcon = (value: string): string =>
  value.startsWith('fvIcon') ? `assets/svg-icons/${value}.svg#${value}` : tenantPhosphorPath(value);

const TUI_SPECIFIC_PROVIDERS: Array<Provider | EnvironmentProviders> = [
  NG_EVENT_PLUGINS,
  {
    provide: TUI_ICON_RESOLVER,
    useValue: (name: string) => {
      // A tenant override wins over the shared icon, checked ahead of the phosphor/bespoke split so
      // it reaches both fvIcon* and @tui.* menu items.
      const tenantOverride = TENANT_ICON_NAME_OVERRIDES[name];
      if (tenantOverride) {
        return tenantOverride;
      }
      // Multi-color image icons (@img.fvIcon*) render as a full-color <img>; keep the bespoke SVG.
      if (name.startsWith('@img.fvIcon')) {
        const raw = name.replace('@img.', '');
        return `assets/svg-icons/${raw}.svg#${raw}`;
      }
      // Custom single-color fvIcon*: remap generic glyphs to Phosphor, else use the bespoke SVG.
      if (name.startsWith('fvIcon')) {
        const phosphor = FVICON_TO_PHOSPHOR[name];
        return phosphor ? resolveMappedIcon(phosphor) : `assets/svg-icons/${name}.svg#${name}`;
      }
      // Taiga/lucide '@tui.<name>': remap to Phosphor per the Figma Token DS, else fall back to lucide.
      if (name.startsWith('@tui.')) {
        const kebab = name.replace('@tui.', '');
        const phosphor = LUCIDE_TO_PHOSPHOR[kebab];
        return phosphor ? resolveMappedIcon(phosphor) : `assets/taiga-ui/icons/${kebab}.svg`;
      }
      // Legacy 'tuiIcon<Name>': normalize to lucide-kebab (hyphenate before digits, e.g. Edit3 -> edit-3),
      // then remap to Phosphor, else fall back to lucide.
      if (name.startsWith('tuiIcon')) {
        const kebab = name
          .replace('tuiIcon', '')
          .replace(/([a-zA-Z])(\d)/g, '$1-$2')
          .replace(/([A-Z])/g, '-$1')
          .toLowerCase()
          .replace(/^-/, '');
        const phosphor = LUCIDE_TO_PHOSPHOR[kebab];
        return phosphor ? resolveMappedIcon(phosphor) : `assets/taiga-ui/icons/${kebab}.svg`;
      }
      return name;
    },
  },
  {
    provide: TUI_DATE_FORMAT,
    useFactory: () =>
      of({
        mode: inject(LOCALE_ID) === 'en-US' ? 'MDY' : 'DMY',
        separator: '/',
      }),
  },
  TUI_LANGUAGE_PROVIDER,
  provideFvUiKitDefaultOptions(),
  {
    // The grid ships English defaults; resolving them here is what makes every table's empty state
    // follow the active language. Read once on first grid creation, by which point
    // `preloadAllTranslationScopes` has the bundle in place.
    provide: FV_DATA_GRID_TEXTS,
    useFactory: (): FvDataGridTexts => {
      const translocoService = inject(TranslocoService);

      return {
        noData: translocoService.translate('finverity-ui-kit.dataGrid.noDataDefault'),
        noDataForFilters: translocoService.translate('finverity-ui-kit.dataGrid.noDataForFilters'),
        filtersLabel: translocoService.translate('finverity-ui-kit.dataGrid.filtersLabel'),
      };
    },
  },
];

const NGRX_SPECIFIC_PROVIDERS: Array<Provider | EnvironmentProviders> = [
  provideStore(
    {
      [authFeature.name]: authFeature.reducer,
      [userActivityFeature.name]: userActivityFeature.reducer,
    },
    {
      runtimeChecks: {
        strictActionSerializability: false,
        strictActionTypeUniqueness: true,
        strictActionImmutability: true,
        strictActionWithinNgZone: true,
        strictStateSerializability: false,
        strictStateImmutability: true,
      },
      metaReducers: [debugActionsMetaReducer],
    }
  ),
  provideEffects(authEffects, userActivityEffects),
  // Error: NG0200: Circular dependency in DI detected for ErrorHandler. Find more at https://angular.io/errors/NG0200
  // provideStoreDevtools({ name: 'Finverity Platform App' }),
];

const APP_SPECIFIC_PROVIDERS: Array<Provider | EnvironmentProviders> = [
  provideFinAuthenticationConfiguration(() => {
    const environmentService = inject(FinEnvironmentService);
    return {
      isDevelopment: !environmentService.production,
      SSOUrl: environmentService.api.SSOUrl,
      maxUserInactivityMinutes: environmentService.api.maxUserInactivityMinutes,
      platform: {
        companyName: environmentService.platform.company,
        companyAddress: environmentService.platform.companyAddress,
        displayUrl: environmentService.platform.displayUrl,
        logo: environmentService.platform.logo.default,
        supportEmail: environmentService.platform.email,
        isSsoAuthEnabled: environmentService.platform.isSsoAuthEnabled,
        isForceAuthEnabled: !!environmentService.platform.isForceAuthEnabled,
        isLdapAuthEnabled: !!environmentService.platform.isLdapAuthEnabled,
        defaultPhoneCountryCode: environmentService.platform.defaultPhoneCountryCode,
      },
    };
  }),
  provideFinFileUploaderConfiguration(() => {
    const environmentService = inject(FinEnvironmentService);
    return {
      fileServerUrl: environmentService.api.fileServerUrl,
      maxFileSizeBytes: FILE_UPLOAD_RULES.maxFileSize,
    };
  }),
  provideFinIdentityAccessPolicies(() => inject(AuthenticationFacadeService).userPolicies$ as Observable<Policy[]>),
  provideFinIdentityAccessPermissions(
    () => inject(AuthenticationFacadeService).userPermissions$ as Observable<Permission[]>
  ),
  provideFinApolloGraphqlOptions(() => {
    const environmentService = inject(FinEnvironmentService);
    const notificationsService = inject(NotificationsService);
    const sentryReporterService = inject(SentryReporterService);
    const mfaLinkFactory = inject(MfaApolloLinkFactory);

    return {
      apiUrl: environmentService.api.gatewayUrl,
      wsUrl: environmentService.api.gatewayWSUrl,
      graphqlErrorNotifier: notificationsService,
      graphqlErrorReporter: sentryReporterService,
      logoutFactory: () => {
        const authFacade = inject(AuthenticationFacadeService);
        return () => authFacade.logout();
      },
      authTokenFactory: () => {
        const authFacade = inject(AuthenticationFacadeService);
        const authTokenStorageService = inject(AuthenticationTokenStorageService);
        return authFacade.lastTokenRefreshTime$.pipe(map(() => authTokenStorageService.getToken()));
      },
      additionalLinks: [mfaLinkFactory.create()],
    };
  }),
  provideActiveDirectoryAuthDataAccess(),
  {
    provide: CONTRACT_ACTIONS_CURRENT_COMPANY_ID,
    useFactory: () => {
      const authFacade = inject(AuthenticationFacadeService);
      return () => authFacade.userSignal()?.company_id ?? null;
    },
  },
  {
    provide: SAFE_LINK_ALLOWED_DOMAINS,
    useFactory: () => getAllowedDomainsList(inject(FinEnvironmentService)),
  },
  {
    provide: ErrorHandler,
    useFactory: () =>
      inject(FinEnvironmentService).sentry.enabled ? inject(SentryErrorHandlerService) : new ErrorHandler(),
  },
  provideAppInitializer(() => {
    const tokenStorage = inject(AuthenticationTokenStorageService);
    const currenciesService = inject(CurrenciesService);

    if (tokenStorage.getToken()) {
      // Deliberately not returned: the request has to overlap the auth bootstrap, not delay it.
      currenciesService.loadCurrencyConfigurationOnce().subscribe();
    }
  }),
  // Kerberos page-load auto-login MUST run before `initAuthUser` so that any
  // token it stores is visible when the auth check runs.
  //
  // `initAuthUser()` returns an Observable, not a Promise — awaiting it
  // directly is a no-op (Observables aren't thenables) and the bootstrap
  // would continue before the user profile load resolves, so route guards
  // would see "not authenticated" on reload and bounce to /login. Wrap in
  // `firstValueFrom` to actually block on the success/failure action. The
  // `defaultValue` handles the no-token branch where `initAuthUser` returns
  // EMPTY — without it firstValueFrom rejects with an EmptyError.
  provideAppInitializer(async () => {
    const authFacadeService = inject(AuthenticationFacadeService);
    const adAuth = inject(AD_AUTH_TOKEN);
    const tokenStorage = inject(AuthenticationTokenStorageService);
    const environmentService = inject(FinEnvironmentService);
    if (environmentService.platform.isKerberosAuthEnabled) {
      await attemptKerberosAutoLogin(adAuth, tokenStorage);
    }
    await firstValueFrom(authFacadeService.initAuthUser(), { defaultValue: undefined });
  }),
  // Firebase Cloud Messaging — opt-out for air-gap; FCMService DI requires
  // provideFirebaseApp/provideMessaging which are not registered in that case.
  ...(environment.firebase.enabled === false
    ? []
    : [
        provideAppInitializer(() => {
          const messagingService = inject(FirebaseCloudMessagingService);
          return messagingService.init();
        }),
      ]),
  provideAppInitializer(() => {
    const stonlyService = inject(StonlyService);
    return stonlyService.initialize();
  }),
];

export const appConfig: ApplicationConfig = {
  providers: [ANGULAR_SPECIFIC_PROVIDERS, TUI_SPECIFIC_PROVIDERS, NGRX_SPECIFIC_PROVIDERS, APP_SPECIFIC_PROVIDERS],
};
